/**
 * @name InterceptorHTTP
 * @description Interceptor Axios HTTP Middleware
 * @author Ricardo Guerra
 */

import React from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import { Service } from 'axios-middleware';

import { Environment } from '@config';
import { LoadingTopBar } from '@components';
import { PreviousRouteService } from '@services';
import { refreshToken } from '@services/TokenManager';

const InterceptorHTTP = new Service(axios);

// Request List
let requestQueue = [];
let isFirstEntry = false; // Flag to know is Login first time
let isRefreshingToken = false; // Flag to know is refreshing token

/**
 * NOTA:
 * (1) Si el token está vencido pero el refresh token está dentro del rango, pedir autenticación con refresh token.
 * (2) Si el token está vencido y el refresh token también está vencido y hay credenciales (keepSession), entonces pedir autenticación con credenciales.
 * (3) Si el token está vencido y el refresh token también está vencido, y no hay credenciales, entonces Logout.
 *
 * Esta lógica ahora vive en TokenManager. Aquí solo coordinamos:
 *  - Cola de requests mientras se refresca
 *  - Reintento del request original con el nuevo token
 */

// Axios Middleware API
InterceptorHTTP.register({
  onRequest(config) {
    return requestHandler(config);
  },
  onRequestError(error) {
    return Promise.reject(error);
  },
  onSync(promise) {
    return promise;
  },
  onResponse(response) {
    return successHandler(response);
  },
  onResponseError(error) {
    return errorHandler(error);
  }
});

// Resolve Process RequestQueue
const processQueue = (error, accessToken = null) => {
  requestQueue.forEach((promise) => {
    if (error) {
      promise.reject(error);
    } else {
      promise.resolve(accessToken);
    }
  });

  requestQueue = [];
};

/**
 * Ejecución de cada SUCCESS de response HTTP
 * @param {any} response Response HTTP Axios
 * @return {any} response
 */
const successHandler = (response) => {
  ReactDOM.render(
    <LoadingTopBar />,
    document.getElementById('loading-bar')
  ).complete();

  return response;
};

/**
 * Ejecución de cada ERROR de response HTTP
 * @param {any} error Error HTTP Axios
 * @return {any} config
 */
const errorHandler = (error) => {
  ReactDOM.render(
    <LoadingTopBar />,
    document.getElementById('loading-bar')
  ).complete();

  const request = error?.config;
  const currentEnvironment = Environment.getCurrent();
  const authUrl = currentEnvironment?.endpoints?.auth;

  // ERROR 401 (Unauthorized) Token
  if (
    error?.response &&
    error.response.status === 401 &&
    request &&
    !request._isRetryRequest &&
    request.url !== authUrl // no intentes refrescar si el 401 es del endpoint de auth
  ) {
    // Si ya hay un refresh en curso, guardar request en cola y esperar
    if (isRefreshingToken) {
      return new Promise((resolve, reject) => {
        requestQueue.push({ resolve, reject });
      })
        .then((accessToken) => {
          request.headers = request.headers || {};
          request.headers['Authorization'] = `Bearer ${accessToken}`;
          return axios(request);
        });
    }

    // Marcar el request como reintento para evitar loops
    request._isRetryRequest = true;
    isRefreshingToken = true;

    // Delegar la estrategia completa de refresh al TokenManager
    return new Promise((resolve, reject) => {
      refreshToken({ allowLegacyLogin: true, credentialScope: 'keepSession' })
        .then((accessToken) => {
          // Inyectar nuevo token al request original
          request.headers = request.headers || {};
          request.headers['Authorization'] = `Bearer ${accessToken}`;

          // Despertar la cola con el nuevo token
          processQueue(null, accessToken);

          // Reintentar request original
          resolve(axios(request));
        })
        .catch((err) => {
          // Si el refresh/fallback falló, rechaza todos los pendientes
          processQueue(err, null);
          reject(err);
        })
        .finally(() => {
          isRefreshingToken = false;
        });
    });
  } else if (!error?.response) {
    // Error de red (sin response)
    // Mantengo el comportamiento previo: rechazar con null
    return Promise.reject(null);
  }

  return Promise.reject(error);
};

/**
 * Ejecución de cada request HTTP
 * @param {any} config Request HTTP Axios
 * @return {any} config
 */
const requestHandler = (config) => {
  ReactDOM.render(
    <LoadingTopBar />,
    document.getElementById('loading-bar')
  ).start();

  const currentEnvironment = Environment.getCurrent();

  const isHTMLRequest = ((URL) => {
    const parts = URL.split('.');
    return parts[parts.length - 1] === 'html';
  })(config.url);

  // Si es Login por primera vez y viene de correo, el URL será guardado para después redireccionar
  if (isFirstEntry === false) {
    if (window.location.href.substr(window.location.href.length - 2) === '?1') {
      PreviousRouteService.setPreviousRoute(
        `/${window.location.href.slice(0, -2).split('#')[1].substr(1)}`
      );
    }
    isFirstEntry = true;
  }

  // Si el request config ya tiene atributo 'Authorization', retornar config original
  if (typeof config.headers?.Authorization !== 'undefined') {
    return config;
  }

  // Si es request HTML ó de Token, retornar config original
  if (config.url === currentEnvironment.endpoints.auth || isHTMLRequest) {
    return config;
  }

  // Si el token aún está vivo, añadir headers; si no, dejarlo pasar y que el 401 dispare el refresh en errorHandler
  const authData = safelyParseLS('ls.authorizationData');
  const isTokenAlive =
    authData && authData.expirationDate && (new Date() < new Date(authData.expirationDate));

  if (authData?.token && isTokenAlive) {
    return setHeaders(config);
  } else {
    return config;
  }
};

/**
 * Añade headers a todos los request HTTP (Token y Lenguaje)
 * @param {object} config Request HTTP Axios
 * @return {any} config
 */
const setHeaders = (config) => {
  config.headers = config.headers || {};

  const token = safelyParseLS('ls.authorizationData')?.token;
  const language = safelyParseLS('ls.ty-Language')?.language;

  if (token) {
    config.headers['Authorization'] = `Bearer ${token}`;
  }

  if (language) {
    config.headers['Accept-Language'] = language;
  }

  return config;
};

// Helper para parsear localStorage sin romper
const safelyParseLS = (key) => {
  try { return JSON.parse(localStorage.getItem(key)); }
  catch { return null; }
};

export default InterceptorHTTP;
